import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const EMPTY = { name: "", short: "", type: "", description: "", website: "", logo_url: "", status: "active", order: 99, disclosure: "" };

export default function Partners() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get("/admin/partners").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const open = (p) => { setEditing(p || {}); setForm(p || EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing?.id) await api.put(`/admin/partners/${editing.id}`, form);
      else await api.post("/admin/partners", form);
      toast.success("Lender saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this lender from the website?")) return;
    await api.delete(`/admin/partners/${id}`);
    toast.success("Lender removed");
    load();
  };

  return (
    <div data-testid="admin-partners-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-brand-dark">Lenders & Partners</h1>
          <p className="text-sm text-brand-muted mt-0.5">Only active lenders appear on the public website</p>
        </div>
        <button onClick={() => open(null)} data-testid="add-partner-btn" className="btn-primary !px-5 !py-2.5 !text-xs"><Plus size={14} /> Add Lender</button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white rounded-xl border border-black/5 p-5 mb-5 grid sm:grid-cols-3 gap-3" data-testid="partner-form">
          <input required className="input-site" placeholder="Lender Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-name" />
          <input required className="input-site" placeholder="Short label (e.g. HDFC) *" value={form.short} onChange={(e) => setForm({ ...form, short: e.target.value })} data-testid="partner-short" />
          <select className="input-site" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="partner-type">
            <option value="">Type</option>
            {["Public Sector Bank", "Private Bank", "NBFC", "Housing Finance Company", "Small Finance Bank"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <input className="input-site" placeholder="Website URL" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
          <input className="input-site" placeholder="Logo URL (e.g. /assets/partners/hdfc.png)" value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} data-testid="partner-logo" />
          <input type="number" className="input-site" placeholder="Display order" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} />
          <select className="input-site" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="partner-status">
            <option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
          <textarea className="input-site sm:col-span-3 min-h-[70px]" placeholder="Description / products offered" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <textarea className="input-site sm:col-span-3 min-h-[50px]" placeholder="Disclosure text" value={form.disclosure} onChange={(e) => setForm({ ...form, disclosure: e.target.value })} />
          <div className="sm:col-span-3 flex gap-2">
            <button type="submit" data-testid="partner-save-btn" className="btn-primary !px-5 !py-2.5 !text-xs">Save Lender</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline !px-4 !py-2.5 !text-xs"><X size={13} /></button>
          </div>
        </form>
      )}

      {!rows ? <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-brand-red" size={28} /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-black/5 p-5" data-testid={`partner-row-${p.short?.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {p.logo_url && <img src={p.logo_url} alt={`${p.name} logo`} className="h-8 w-auto max-w-[90px] object-contain" />}
                  <div>
                    <p className="font-heading font-extrabold text-lg text-brand-dark">{p.short}</p>
                    <p className="text-xs text-brand-muted mt-0.5">{p.name}</p>
                  </div>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${p.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>{p.status}</span>
              </div>
              <p className="text-xs text-brand-muted mt-3">{p.type} · order {p.order}</p>
              <div className="flex gap-3 mt-4 pt-3 border-t border-black/5">
                <button onClick={() => open(p)} data-testid={`partner-edit-${p.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-brand-dark hover:text-brand-red"><Pencil size={12} /> Edit</button>
                <button onClick={() => remove(p.id)} data-testid={`partner-delete-${p.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-brand-muted hover:text-brand-red"><Trash2 size={12} /> Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
